// The allergen gate before a basket is confirmed, shared by the standard basket and chat/voice ordering on pilot
// restaurants. The server (POST /support/v1/order-intents/basket-check) reads the live menu and answers with the
// current prices and stock, problems and changes, the allergy question, allergen advice in words and a quote hash.
// A basket is placed only after a check that is confirmable for the exact quote the customer saw (and, in voice,
// heard to the end). Missing information is unknown, never allergen-free; nothing here decides what is safe.
import { checkBasket } from './supportApi';

// The customer's answer to "any allergies or dietary requirements?", per restaurant, for this page session only.
const answers = {};
// Bumped on every set or clear, so a check started under one answer is never used once the answer has moved.
const revisions = {};

export function getAllergyAnswer(restaurantId) {
  return answers[restaurantId] || null;
}

export function getAllergyRevision(restaurantId) {
  return revisions[restaurantId] || 0;
}

// { none: true } or { text: 'allergic to milk' } (read by the server against the restaurant's own key), or
// the answer the server parsed from the customer's words ({ answered, none, codes, other }).
export function setAllergyAnswer(restaurantId, answer) {
  if (restaurantId === undefined || restaurantId === null) return;
  revisions[restaurantId] = (revisions[restaurantId] || 0) + 1;
  if (!answer) delete answers[restaurantId];
  else answers[restaurantId] = answer;
}

export function allergyForRequest(answer) {
  if (!answer) return undefined;
  if (answer.text) return { text: answer.text };
  if (answer.none) return { answered: true, none: true };
  return { answered: true, none: false, codes: answer.codes || [], other: answer.other || undefined };
}

function optionIdsOf(product) {
  return (product.mealOptions || [])
    .map(option => option.idMealOption)
    .filter(id => id !== undefined && id !== null)
    .sort((a, b) => a - b);
}

export function lineKey(productId, optionIds) {
  return `${productId}:${(optionIds || []).slice().sort((a, b) => a - b).join(',')}`;
}

// The basket as lines: one per dish and choice set, with how many and the unit price the customer sees.
export function cartLines(products) {
  const lines = [];
  (products || []).forEach(product => {
    if (!product || product.id === undefined) return;
    const optionIds = optionIdsOf(product);
    const key = lineKey(product.id, optionIds);
    const line = lines.find(candidate => candidate.key === key);
    if (line) line.qty += 1;
    else lines.push({ key, productId: product.id, qty: 1, optionIds, unitPrice: product.price });
  });
  return lines.map(({ key, ...line }) => line);
}

export function runBasketCheck({ restaurantId, products, quoteHash, seenAdviceText }) {
  return checkBasket({
    restaurantId,
    lines: cartLines(products),
    allergy: allergyForRequest(getAllergyAnswer(restaurantId)),
    quoteHash,
    seenAdviceText,
  });
}

// Everything but the customer's own confirmation is in place: the check only waits for them to confirm this quote.
export function readyToConfirm(check) {
  return (
    !!check &&
    check.applies === true &&
    Array.isArray(check.blockers) &&
    check.blockers.length > 0 &&
    check.blockers.every(blocker => blocker === 'NOT_ACKNOWLEDGED')
  );
}

// A chat or voice order can be confirmed: the check waits only for the customer, nothing is being checked, and the
// customer ticked that they read the written allergen advice for this exact quote (any change clears the tick).
export function voiceConfirmAllowed(order) {
  return (
    !!order &&
    !order.checking &&
    !order.adviceStale &&
    readyToConfirm(order.check) &&
    order.ackQuote === order.check.quoteHash
  );
}

// Why a check can't be confirmed yet, in words, or '' when it only waits for the customer.
export function blockedReason(check) {
  if (!check || check.applies !== true) return '';
  const blockers = check.blockers || [];
  if (blockers.includes('ITEMS')) {
    return (check.problems || []).map(problem => problem.message).join(' ') || 'Your basket is empty.';
  }
  if (blockers.includes('ALLERGY_QUESTION')) return check.allergyQuestion || '';
  if (blockers.includes('RESTAURANT_CONFIRMATION')) {
    return 'Some dishes need the restaurant to confirm they suit your allergy or diet before you order them. Remove them, or contact the restaurant first.';
  }
  if (blockers.includes('CHANGED')) {
    return (check.changes || []).map(change => change.message).join(' ');
  }
  return '';
}

// Not used while the pilot is written-only (owner, 3 Nov). Speaks the advice; resolves { completed: true } only
// when the whole text was spoken. Cancelled, interrupted,
// failed or unsupported speech resolves { completed: false }, so confirmation stays blocked.
export function speakAdvice(text, speech = typeof window !== 'undefined' ? window.speechSynthesis : null) {
  const Utterance = typeof window !== 'undefined' ? window.SpeechSynthesisUtterance : null;
  if (!speech || !Utterance || !text) return Promise.resolve({ completed: false, reason: 'unsupported' });
  return new Promise(resolve => {
    let settled = false;
    const finish = result => {
      if (settled) return;
      settled = true;
      resolve(result);
    };
    const utterance = new Utterance(text);
    utterance.lang = 'en-IE';
    utterance.onend = () => finish({ completed: true });
    utterance.onerror = event =>
      finish({ completed: false, reason: (event && event.error) || 'error' });
    speech.cancel();
    speech.speak(utterance);
  });
}

export function stopSpeaking(speech = typeof window !== 'undefined' ? window.speechSynthesis : null) {
  if (speech) speech.cancel();
}

// Restaurants in the allergen-gated ordering pilot, from the build (REACT_APP_ALLERGEN_GATE_RESTAURANTS=8630).
// Only these call the check, and on these a failed check blocks the order: there is no stale fallback.
export function allergenGateApplies(
  restaurantId,
  ids = process.env.REACT_APP_ALLERGEN_GATE_RESTAURANTS || ''
) {
  if (restaurantId === undefined || restaurantId === null) return false;
  return ids
    .split(',')
    .map(id => id.trim())
    .filter(Boolean)
    .includes(String(restaurantId));
}



// WEBPACK FOOTER //
// ./src/support/basketCheck.js